import type { ClipboardData } from '@/models/clipboard/ClipboardData';
import { orderedEdit, orderedEditResult } from '@/simulation/native/authority';
import type { ChipInstanceId } from '@/types/ids';
import { makeAutoObservable, observable } from 'mobx';
import React from 'react';

import type {
  DeleteChipDialogState,
  TileColorDialogState,
  GenericInputDialogState,
  InterfaceRenameDialogState,
  IChipModel,
  ICircuitModel,
  IUiModel,
  StackDialogState,
  LabelSelectionDialogState,
  ValidateFn,
} from '@/interfaces/models';
import { AppModelContext } from '@/models/AppModel';
import { InputType } from './tiles/TileDefinition';
import type { InterfaceLink, SIInputMode } from './ChipModel';
import type { PixelArtData } from './PixelArtData';
import type { TileInstance } from './tiles/TileInstance';

export interface StructurePixelArtDialogState {
  color: string;
  pixelArt: PixelArtData | null;
  onSave: (result: { color: string; pixelArt: PixelArtData | null }) => void;
}

export enum Theme {
  LIGHT = 'light',
  DARK = 'dark',
}

export class UiModel implements IUiModel {
  connectionWarning: { message: string; sequence: number } | null = null;
  connectionGestureSequence = 0;
  connectionGestureActive = false;
  private connectionGestureWarned = false;
  private connectionWarningSequence = 0;

  beginConnectionGesture() {
    this.connectionGestureActive = true;
    this.connectionGestureSequence++;
    this.connectionGestureWarned = false;
  }
  endConnectionGesture() {
    this.connectionGestureActive = false;
    this.connectionGestureWarned = false;
  }
  warnConnection(message: string) {
    if (this.connectionGestureWarned) return;
    this.connectionGestureWarned = true;
    this.connectionWarning = { message, sequence: ++this.connectionWarningSequence };
  }
  clearConnectionWarning() {
    this.connectionWarning = null;
  }

  isChipDraftDialogOpen = false;
  theme: Theme = Theme.DARK;
  isSettingsDialogOpen = false;
  isHelpDialogOpen = false;
  tileColorDialogState: TileColorDialogState | null = null;
  isCoordinateDialogOpen = false;
  isChipDetailsDialogOpen = false;
  debugModeEnabled = false;
  isGraphDebugDialogOpen = false;
  inspectedChipInstanceId: ChipInstanceId | null = null;
  isConnectionInspectorOpen = false;
  connectionInspectorTileId: string | null = null;
  bundleInspectorState: { tile: TileInstance; circuit: ICircuitModel } | null = null;
  editingChip: IChipModel | null = null;
  isEditDelayDialogOpen = false;
  editingDelayTile: any | null = null;
  genericInputDialogState?: GenericInputDialogState;
  interfaceRenameDialogState?: InterfaceRenameDialogState;
  miStateConfigLink: InterfaceLink | null = null;
  miConnectionConfigLink: InterfaceLink | null = null;
  siInputModeState: {
    currentMode: SIInputMode;
    onSelect: (mode: SIInputMode) => void;
  } | null = null;
  labelSelectionDialogState: LabelSelectionDialogState | null = null;
  templateCapture: { data: ClipboardData; templateType: 'logic' | 'structure' } | null =
    null;
  stackDialogState: StackDialogState | null = null;
  deleteChipDialogState: DeleteChipDialogState | null = null;
  structurePixelArtState: StructurePixelArtDialogState | null = null;

  constructor() {
    makeAutoObservable(
      this,
      { bundleInspectorState: observable.ref, labelSelectionDialogState: observable.ref },
      { autoBind: true },
    );
  }

  openCoordinateDialog() {
    this.isCoordinateDialogOpen = true;
  }

  closeCoordinateDialog() {
    this.isCoordinateDialogOpen = false;
  }

  openSettingsDialog() {
    return orderedEdit(this, () => {
      this.isSettingsDialogOpen = true;
    });
  }

  closeSettingsDialog() {
    this.isSettingsDialogOpen = false;
  }

  openHelpDialog() {
    this.isHelpDialogOpen = true;
  }

  closeHelpDialog() {
    this.isHelpDialogOpen = false;
  }

  inspectChipInstance(id: ChipInstanceId | null) {
    return orderedEdit(this, () => {
      this.inspectedChipInstanceId = id;
    });
  }

  setDebugModeEnabled(enabled: boolean) {
    this.debugModeEnabled = enabled;
    if (!enabled) {
      this.closeGraphDebugDialog();
      this.closeConnectionInspector();
    }
  }

  openConnectionInspector(tileId: string | null = null) {
    if (!this.debugModeEnabled) return;
    return orderedEdit(this, () => {
      this.connectionInspectorTileId = tileId;
      this.isConnectionInspectorOpen = true;
    });
  }

  closeConnectionInspector() {
    this.isConnectionInspectorOpen = false;
    this.connectionInspectorTileId = null;
  }

  openBundleInspector(tile: TileInstance, circuit: ICircuitModel) {
    return orderedEdit(this, () => {
      this.bundleInspectorState = { tile, circuit };
    });
  }

  closeBundleInspector() {
    this.bundleInspectorState = null;
  }

  openGraphDebugDialog() {
    if (!this.debugModeEnabled) return;
    return orderedEdit(this, () => {
      this.isGraphDebugDialogOpen = true;
    });
  }

  closeGraphDebugDialog() {
    this.isGraphDebugDialogOpen = false;
  }

  openEditChipDialog(chip: IChipModel) {
    return orderedEdit(this, () => {
      this.editingChip = chip;
      this.isChipDetailsDialogOpen = true;
    });
  }

  closeEditChipDialog() {
    this.isChipDetailsDialogOpen = false;
    this.editingChip = null;
  }

  openTileColorDialog(state: TileColorDialogState) {
    return orderedEdit(this, () => {
      this.tileColorDialogState = state;
    });
  }
  closeTileColorDialog() {
    this.tileColorDialogState = null;
  }

  openEditDelayDialog(tile: any) {
    return orderedEdit(this, () => {
      this.editingDelayTile = tile;
      this.isEditDelayDialogOpen = true;
    });
  }

  closeEditDelayDialog() {
    this.isEditDelayDialogOpen = false;
    this.editingDelayTile = null;
  }

  openGenericInputDialog(
    type: InputType,
    prompt: string,
    defaultValue: string | number,
    isCancellable: boolean,
    validateFn?: ValidateFn,
    suggestions?: string[],
  ): Promise<string | number> {
    return orderedEditResult(
      this,
      () =>
        new Promise<string | number>((resolve, reject) => {
          this.genericInputDialogState = {
            isOpen: true,
            isCancellable,
            inputType: type,
            prompt,
            defaultValue,
            resolve,
            reject,
            validateFn,
            suggestions,
          };
        }),
    );
  }

  openInterfaceRenameDialog(
    label: string,
    sideLabels: Record<string, string>,
    isMulti: boolean,
  ): Promise<{ label: string; sideLabels: Record<string, string> }> {
    return orderedEditResult(
      this,
      () =>
        new Promise((resolve, reject) => {
          this.interfaceRenameDialogState = {
            isOpen: true,
            label,
            sideLabels: { ...sideLabels },
            isMulti,
            resolve,
            reject,
          };
        }),
    );
  }

  openSIInputModeDialog(currentMode: SIInputMode, onSelect: (mode: SIInputMode) => void) {
    return orderedEdit(this, () => {
      this.siInputModeState = { currentMode, onSelect };
    });
  }

  closeSIInputModeDialog() {
    this.siInputModeState = null;
  }

  openMIStateConfigDialog(link: InterfaceLink) {
    return orderedEdit(this, () => {
      this.miStateConfigLink = link;
    });
  }

  closeMIStateConfigDialog() {
    this.miStateConfigLink = null;
  }

  openMIConnectionConfigDialog(link: InterfaceLink) {
    return orderedEdit(this, () => {
      this.miConnectionConfigLink = link;
    });
  }

  closeMIConnectionConfigDialog() {
    this.miConnectionConfigLink = null;
  }

  openLabelSelectionDialog(state: LabelSelectionDialogState) {
    if (
      state.context.editor.currentChipEditor?.currentCircuitEditor !==
        state.circuitEditorModel ||
      state.context.editor.openingChipId
    )
      return;
    this.labelSelectionDialogState = {
      ...state,
      selectionBounds: { ...state.selectionBounds },
    };
  }

  closeLabelSelectionDialog() {
    this.labelSelectionDialogState = null;
  }

  openStackDialog(state: StackDialogState) {
    this.stackDialogState = state;
  }

  closeStackDialog() {
    this.stackDialogState = null;
  }

  openStructurePixelArtDialog(state: StructurePixelArtDialogState) {
    return orderedEdit(this, () => {
      this.structurePixelArtState = state;
    });
  }

  closeStructurePixelArtDialog() {
    this.structurePixelArtState = null;
  }

  openDeleteChipDialog(state: DeleteChipDialogState) {
    return orderedEdit(this, () => {
      this.deleteChipDialogState = state;
    });
  }

  closeDeleteChipDialog() {
    this.deleteChipDialogState = null;
  }

  isChipGroupRenameDialogOpen = false;

  get inDialog(): boolean {
    return (
      this.isChipGroupRenameDialogOpen ||
      this.isChipDraftDialogOpen ||
      this.isCoordinateDialogOpen ||
      this.isSettingsDialogOpen ||
      this.isHelpDialogOpen ||
      this.isChipDetailsDialogOpen ||
      this.isGraphDebugDialogOpen ||
      this.isConnectionInspectorOpen ||
      this.bundleInspectorState !== null ||
      this.inspectedChipInstanceId !== null ||
      this.isEditDelayDialogOpen ||
      this.tileColorDialogState !== null ||
      this.miStateConfigLink !== null ||
      this.miConnectionConfigLink !== null ||
      this.siInputModeState !== null ||
      !!this.templateCapture ||
      this.genericInputDialogState?.isOpen === true ||
      this.interfaceRenameDialogState?.isOpen === true ||
      this.labelSelectionDialogState !== null ||
      this.stackDialogState !== null ||
      this.deleteChipDialogState !== null ||
      this.structurePixelArtState !== null
    );
  }
}

export const useUiModel = (): UiModel => {
  const appModel = React.useContext(AppModelContext);

  if (!appModel) {
    throw new Error('useAppModel must be used within AppModelProvider');
  }

  return appModel.uiModel as UiModel;
};
